import React, { useState, useEffect } from 'react';
import useIsMobile from '../../hooks/useIsMobile';
import { useDispatch } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { Menu } from 'antd';
import {
    PlusSquareOutlined,
    BulbOutlined,
    ReadOutlined,
    UnorderedListOutlined,
    AppstoreOutlined,
    BarChartOutlined,
    BugOutlined,
    SoundOutlined
} from '@ant-design/icons';
// Хук проекта, а не react-i18next напрямую: он тянет за собой инициализацию i18n,
// без которой компонент, отрисованный в обход приложения, показал бы ключи вместо слов.
import useTranslation from '../../hooks/useTranslation';
import { toggleDrawer } from "../../redux/actions/drawerActions";
import FeatureFlagUtil from "../../utils/featureFlagUtil";
import { findAccessToken, hasGroup } from "../../utils/tokenUtils";
import './Drawer.css';

const withIconSize = (IconComponent, size = '20px') => (
    <IconComponent style={{ fontSize: size }} />
);

const Drawer = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const [canShowDebugMenu, setCanShowDebugMenu] = useState(false);
    // Админка по группе токена, а не по фича-флагу: бэкенд закрывает /admin/** ролью ADMIN,
    // и меню лишь повторяет это решение, чтобы не показывать дверь, которая не откроется.
    const [isAdmin, setIsAdmin] = useState(false);


    useEffect(() => {
        try {
            const token = findAccessToken();
            setIsAdmin(Boolean(token) && hasGroup(token, 'ADMIN'));
        } catch (err) {
            console.error("Failed to read groups from token:", err);
        }
    }, []);

    useEffect(() => {
        // Проверяем фичефлаг "DEBUG_MENU"
        FeatureFlagUtil.isFeatureEnabled("DEBUG_MENU")
            .then((enabled) => {
                setCanShowDebugMenu(enabled);
            })
            .catch((err) => {
                console.error("Failed to check DEBUG_MENU feature:", err);
            });
    }, []);

    // Клик по пункту только закрывает меню на телефоне: подсветку задаёт адрес, а не
    // последний клик. Раньше она хранилась отдельным состоянием и застревала на любом
    // переходе мимо меню — кнопкой «назад», по аватару, ссылкой внутри страницы.
    const handleMenuClick = () => {
        if (isMobile) {
            dispatch(toggleDrawer());
        }
    };

    // Динамические страницы с переводами
    const practicePages = [
        { label: t('menu.deck'), icon: withIconSize(AppstoreOutlined), key: 'deck' },
        { label: t('menu.content'), icon: withIconSize(PlusSquareOutlined), key: 'content' },
        { label: t('menu.list'), icon: withIconSize(UnorderedListOutlined), key: 'list' },
        { label: t('menu.learn'), icon: withIconSize(ReadOutlined), key: 'learn' },
        // Столбики, а не решётка: прямо над этим пунктом стоит «Список», и таблица
        // читалась как второй такой же. За иконкой при этом не график, а таблица
        // элементов с их состоянием — столбики выбраны за различимость в меню.
        // Ту же иконку носит «Статистика» (строка ниже, пункт скрыт) и кнопка
        // статистики на странице квиза: если пункт вернут, иконки надо развести.
        { label: t('menu.progress'), icon: withIconSize(BarChartOutlined), key: 'progress' },
        // { label: t('menu.stat'), icon: withIconSize(BarChartOutlined), key: 'stat' },
        { label: t('menu.preset'), icon: withIconSize(BulbOutlined), key: 'preset' },
    ];

    // Служебные пункты синие: видно, что это не часть обучения, а страницы для
    // администратора, и обычный пользователь их не увидит. Цвет целиком в Drawer.css —
    // выделение выбранного пункта antd красит токенами темы, инлайновым стилем его не достать.
    const debugPages = [
        { label: t('menu.debug'), icon: withIconSize(BugOutlined), key: 'debug', admin: true },
    ];

    const adminPages = [
        { label: t('menu.adminAudio'), icon: withIconSize(SoundOutlined), key: 'admin/audio', admin: true },
    ];

    // items, а не вложенные Menu.Item: в antd 5 второе устарело и пишет предупреждение
    // на каждый рендер. Отладочные пункты дописываются к обычным, чтобы разметка пункта
    // жила в одном месте и не расходилась между двумя одинаковыми списками.
    const items = [
        ...practicePages,
        ...(canShowDebugMenu ? debugPages : []),
        ...(isAdmin ? adminPages : []),
    ].map((page) => ({
        key: `/${page.key}`,
        icon: page.icon,
        className: page.admin ? 'admin-menu-item' : undefined,
        label: <Link to={`/${page.key}`}>{page.label}</Link>,
    }));

    return (
        <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            onClick={handleMenuClick}
            items={items}
        />
    );
};

export default Drawer;
